import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ShieldCheck } from "lucide-react";
import { getSettings, getLocations } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const FOOTER_GROUPS = [
  {
    heading: "Preventum Diez", heading_en: "Preventum Diez",
    links: [
      { label: "Was ist Preventum Diez?", en: "What is Preventum Diez?", to: "/preventum-diez/" },
      { label: "Für wen?", en: "Who for?", to: "/preventum-diez/fuer-wen/" },
      { label: "Prävention", en: "Prevention", to: "/praevention/" },
      { label: "Sekundärprävention", en: "Secondary prevention", to: "/sekundaerpraevention/" },
      { label: "Sportmedizin", en: "Sports medicine", to: "/sportmedizin-leistungssport/" },
    ],
  },
  {
    heading: "Medizin", heading_en: "Medicine",
    links: [
      { label: "Kardiologie", en: "Cardiology", to: "/kardiologie-diez/" },
      { label: "Untersuchungen", en: "Examinations", to: "/untersuchungen/" },
      { label: "Belastungs-EKG", en: "Exercise ECG", to: "/untersuchungen/#belastungs-ekg" },
      { label: "Herzultraschall", en: "Echocardiography", to: "/untersuchungen/#herzultraschall" },
      { label: "Spiroergometrie", en: "Spiroergometry", to: "/untersuchungen/#spiroergometrie" },
    ],
  },
  {
    heading: "Wissen", heading_en: "Knowledge",
    links: [
      { label: "Wissenswertes", en: "Good to know", to: "/wissen/" },
      { label: "FAQ", en: "FAQ", to: "/faq/" },
      { label: "Glossar", en: "Glossary", to: "/glossar/" },
      { label: "News", en: "News", to: "/news/" },
      { label: "Sport in jedem Alter", en: "Sport at any age", to: "/wissen/sport-in-jedem-alter/" },
      { label: "Sport nach Herzinfarkt", en: "Sport after a heart attack", to: "/wissen/sport-nach-herzinfarkt/" },
      { label: "Untersuchung vor Sportbeginn", en: "Check before starting sport", to: "/wissen/untersuchung-vor-sportbeginn/" },
    ],
  },
  {
    heading: "Praxis", heading_en: "Practice",
    links: [
      { label: "Dr. Kirchner", en: "Dr. Kirchner", to: "/dr-kirchner/" },
      { label: "Ablauf", en: "How it works", to: "/ablauf/" },
      { label: "Standort Diez", en: "Location Diez", to: "/standort-diez/" },
      { label: "Kontakt", en: "Contact", to: "/kontakt/" },
      { label: "Termin anfragen", en: "Request appointment", to: "/kontakt/#termin" },
    ],
  },
  {
    heading: "Rechtliches", heading_en: "Legal",
    links: [
      { label: "Impressum", en: "Imprint", to: "/impressum/" },
      { label: "Datenschutz", en: "Privacy", to: "/datenschutz/" },
    ],
  },
];

export default function Footer({ onOpenCookieSettings }) {
  const [settings, setSettings] = useState({});
  const [locations, setLocations] = useState([]);
  const { lang, t, lp } = useLang();

  useEffect(() => {
    getSettings(lang).then(setSettings).catch(() => {});
    getLocations(lang).then(setLocations).catch(() => {});
  }, [lang]);

  return (
    <footer className="bg-navy-dark text-white" data-testid="site-footer">
      {/* Top: brand block + 5 columns */}
      <div className="container-x py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 mb-12">
          <div className="lg:col-span-2">
            <img
              src={settings.logo_negative_url || "https://customer-assets.emergentagent.com/job_a2ad48e5-f113-4504-acb4-865147bb1512/artifacts/64ep1xr0_PREVENTUM%20DIEZ%20NEG.svg"}
              alt="Preventum Diez"
              className="h-12 md:h-14 w-auto mb-5 object-contain"
            />
            <p className="text-white/90 text-base font-medium leading-relaxed mb-2">
              {settings.claim || "Klarheit vor Bewegung."}
            </p>
            <p className="text-white/65 text-sm">{settings.short_description}</p>
            {locations[0] && (
              <address className="not-italic text-white/80 text-sm space-y-1 mt-5">
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0" /><span>{locations[0].name}<br />{locations[0].street}, {locations[0].zip} {locations[0].city}</span></div>
                {locations[0].phone && (
                  <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /><a href={`tel:${locations[0].phone.replace(/\s/g,"")}`} className="hover:text-white">{locations[0].phone}</a></div>
                )}
                {locations[0].email && (
                  <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" /><a href={`mailto:${locations[0].email}`} className="hover:text-white break-all">{locations[0].email}</a></div>
                )}
                {settings.whatsapp_number && (
                  <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /><a href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g,"")}`} target="_blank" rel="noopener noreferrer" className="hover:text-white">WhatsApp: {settings.whatsapp_number}</a></div>
                )}
              </address>
            )}
          </div>

          {FOOTER_GROUPS.map((group) => (
            <div key={group.heading} data-testid={`footer-group-${group.heading.toLowerCase()}`}>
              <h3 className="text-white font-semibold mb-3 text-[15px]">{lang === "en" ? group.heading_en : group.heading}</h3>
              <ul className="space-y-2 text-[13.5px]">
                {group.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link to={lp(l.to)} className="text-white/75 hover:text-white transition-colors">{lang === "en" ? (l.en || l.label) : l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 relative">
        <div className="container-x py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm">
          <p className="text-white/65">
            © {new Date().getFullYear()} {settings.site_name || "Preventum Diez"}. {t("rightsReserved")}
          </p>
          <nav className="flex flex-wrap gap-x-5 gap-y-2 items-center">
            <Link to={lp("/impressum/")} className="text-white/75 hover:text-white">{t("imprint")}</Link>
            <Link to={lp("/datenschutz/")} className="text-white/75 hover:text-white">{t("privacy")}</Link>
            <button
              type="button"
              onClick={onOpenCookieSettings}
              className="text-white/75 hover:text-white inline-flex items-center gap-1"
              data-testid="footer-cookie-settings"
            >
              <ShieldCheck className="h-3.5 w-3.5" /> {t("cookieSettings")}
            </button>
          </nav>
        </div>
        {/* discrete CMS access dot bottom-right */}
        <Link
          to="/admin/login"
          aria-label="CMS"
          title="CMS"
          data-testid="footer-admin-link"
          className="absolute bottom-3 right-3 md:bottom-4 md:right-4 h-3 w-3 rounded-full bg-white/15 hover:bg-white/60 transition-all duration-200 hover:scale-150"
        />
      </div>
    </footer>
  );
}
